/**
 * API client — thin fetch wrapper.
 *
 * Throws on non-2xx; routes that need custom error shapes can use the raw
 * `fetch` directly. CSRF token added to mutating requests automatically
 * via the cookie-derived X-CSRF-Token header (per CHAP-67 helmet/CSRF
 * middleware on the server).
 */

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Read CSRF token from cookie (set by server's csrf middleware as a
 * double-submit cookie). Mutating requests must echo it back via
 * X-CSRF-Token header.
 *
 * CHAP-594: production names the cookie `__Host-chapterbuild_csrf`; dev and
 * e2e keep `chapterbuild_csrf`. The `__Host-` name wins when both exist. This
 * is the only reader of the token: every caller goes through it.
 *
 * document.cookie separates cookies with exactly "; ". The separator was
 * `;\s*`, but \s also matches U+00A0, so a sibling-set cookie named
 * "<U+00A0>__Host-chapterbuild_csrf" was read as ours (review N1).
 */
const CSRF_COOKIE_PATTERNS = [
  /(?:^|; )__Host-chapterbuild_csrf=([^;]+)/,
  /(?:^|; )chapterbuild_csrf=([^;]+)/,
];

export function readCsrfToken(): string | null {
  if (typeof document === 'undefined') return null;
  for (const pattern of CSRF_COOKIE_PATTERNS) {
    const m = document.cookie.match(pattern);
    if (m) return decodeURIComponent(m[1]);
  }
  return null;
}

function csrfHeaders(): Record<string, string> {
  const token = readCsrfToken();
  return token ? { 'X-CSRF-Token': token } : {};
}

/**
 * CHAP-594 review N5: a mutation refused with 403 csrf_validation_failed
 * (its cookie expired, or predates the __Host- name) gets one GET of the
 * session endpoint, which lets the server issue a fresh cookie, and is then
 * retried exactly once, never more. The server refuses a CSRF failure before
 * doing anything, so the retry is safe. `init` is rebuilt for the retry so it
 * carries the fresh token.
 */
const CSRF_REFRESH_PATH = '/api/me';

async function isCsrfRejection(res: Response): Promise<boolean> {
  if (res.status !== 403) return false;
  try {
    const body = (await res.clone().json()) as { error?: unknown } | null;
    return body?.error === 'csrf_validation_failed';
  } catch {
    return false;
  }
}

async function sendMutation(path: string, init: () => RequestInit): Promise<Response> {
  const res = await fetch(path, init());
  if (!(await isCsrfRejection(res))) return res;
  await fetch(CSRF_REFRESH_PATH, {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
    .then((r) => r.text())
    .catch(() => undefined);
  return fetch(path, init());
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      body = await res.text();
    }
    throw new ApiError(res.status, body, `${res.url} failed: ${res.status}`);
  }
  if (res.status === 204) return undefined as unknown as T;
  return (await res.json()) as T;
}

export async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(path, {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  return handleResponse<T>(res);
}

export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  const res = await sendMutation(path, () => ({
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...csrfHeaders(),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  }));
  return handleResponse<T>(res);
}

export async function apiPatch<T>(path: string, body: unknown): Promise<T> {
  const res = await sendMutation(path, () => ({
    method: 'PATCH',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...csrfHeaders(),
    },
    body: JSON.stringify(body),
  }));
  return handleResponse<T>(res);
}

export async function apiDelete<T>(path: string): Promise<T> {
  const res = await sendMutation(path, () => ({
    method: 'DELETE',
    credentials: 'include',
    headers: { Accept: 'application/json', ...csrfHeaders() },
  }));
  return handleResponse<T>(res);
}
